import React from 'react';
import { useUIStore } from '../../stores/index.ts';
import './Sidebar.css';

interface SidebarProps {
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ className = '' }) => {
  const { 
    panels, 
    openPanel, 
    closePanel,
    closeAllPanels
  } = useUIStore();

  const handleIconClick = (panelName: string) => {
    if (panels[panelName as keyof typeof panels]) {
      // Si el panel está abierto, cerrarlo
      closePanel(panelName as keyof typeof panels);
    } else {
      // Si el panel está cerrado, cerrar todos los demás primero y luego abrir este
      closeAllPanels();
      openPanel(panelName as keyof typeof panels);
    }
  };

  return (
    <div className={`v2-sidebar ${className}`}>
      {/* Barra de iconos lateral */}
      <div className="v2-toolbar">
        <div className="v2-toolbar-icons-container">
          {/* Icono IFC */}
          <div 
            className={`v2-toolbar-icon ${panels.ifcInput ? 'active' : ''}`}
            onClick={() => handleIconClick('ifcInput')}
            title="Importar IFC"
          >
            <svg className="v2-icon" viewBox="0 0 24 24">
              <path d="M19,4H5V6H19M19,15H15V21H9V15H5L12,8L19,15Z"/>
            </svg>
          </div>

          {/* Icono Geolocalización (antes: Catastro) */}
          <div 
            className={`v2-toolbar-icon ${panels.cadasterInput ? 'active' : ''}`}
            onClick={() => handleIconClick('cadasterInput')}
            title="Geolocalización"
          >
            <svg className="v2-icon" viewBox="0 0 24 24">
              <path d="M12,2C15.31,2 18,4.66 18,7.95C18,12.41 12,19 12,19S6,12.41 6,7.95C6,4.66 8.69,2 12,2M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6Z"/>
            </svg>
          </div>

          {/* Icono Herramientas de dibujo */}
          <div 
            className={`v2-toolbar-icon ${panels.drawing ? 'active' : ''}`}
            onClick={() => handleIconClick('drawing')}
            title="Herramientas de dibujo"
          >
            <svg className="v2-icon" viewBox="0 0 24 24">
              <path d="M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
            </svg>
          </div>

          {/* Icono Cálculos */}
          <div 
            className={`v2-toolbar-icon ${panels.calculations ? 'active' : ''}`}
            onClick={() => handleIconClick('calculations')}
            title="Cálculos"
          >
            <svg className="v2-icon" viewBox="0 0 24 24">
              <path d="M7,2H17A2,2 0 0,1 19,4V20A2,2 0 0,1 17,22H7A2,2 0 0,1 5,20V4A2,2 0 0,1 7,2M7,4V8H17V4H7M7,10V12H9V10H7M11,10V12H13V10H11M15,10V12H17V10H15M7,14V16H9V14H7M11,14V16H13V14H11M15,14V16H17V14H15M7,18V20H9V18H7M11,18V20H13V18H11M15,18V20H17V18H15Z"/>
            </svg>
          </div>

          {/* Icono Exportar */}
          <div 
            className={`v2-toolbar-icon ${panels.export ? 'active' : ''}`}
            onClick={() => handleIconClick('export')}
            title="Exportar Elementos"
          >
            <svg className="v2-icon" viewBox="0 0 24 24">
              <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
            </svg>
          </div>

          {/* Icono Configuración */}
          <div 
            className={`v2-toolbar-icon ${panels.configuration ? 'active' : ''}`}
            onClick={() => handleIconClick('configuration')}
            title="Configuración"
          >
            <svg className="v2-icon" viewBox="0 0 24 24">
              <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
